// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Knowledge Base User View Wrapper
 *
 * Switches between findings and report UIs based on the `mode` setting.
 * - 'findings': Full KB editor with categories, sources, scores
 * - 'report': Simplified report section editor with format specs
 *
 * Mode is read from the KnowledgeBaseProvider store (single fetch).
 */
import { type FC } from 'react'
import { Loader2 } from 'lucide-react'

import type { NodeUserViewProps } from '../../../../core/types'
import { KnowledgeBaseProvider, useKnowledgeBaseStore } from '../core/KnowledgeBaseProvider'
import { ViewConfigProvider } from '../core/ViewConfigContext'
import { REPORT_CONFIG } from '../types'
import { resolveSettingValue } from '../utils/settings'

import { KnowledgeBaseScreen } from './UserView'
import { ReportKBScreen } from '../../apps/contract-review/report/components/ReportKBUserView'

interface Props extends NodeUserViewProps {
  wsId?: string
  showHeader?: boolean
}

/**
 * Wrapper that renders KnowledgeBaseProvider once, then routes to the
 * appropriate UI based on the mode setting from the store (no extra fetch).
 */
export const KnowledgeBaseUserViewWrapper: FC<Props> = (props) => {
  return (
    <KnowledgeBaseProvider {...props}>
      <ModeRouter {...props} />
    </KnowledgeBaseProvider>
  )
}

/**
 * Reads mode from the already-loaded store and renders the correct view.
 */
const ModeRouter: FC<Props> = (props) => {
  const store = useKnowledgeBaseStore()

  if (store.loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const mode = resolveSettingValue(store.settings, 'mode')

  if (mode === 'report') {
    return (
      <ViewConfigProvider config={REPORT_CONFIG}>
        <ReportKBScreen workspaceLabel={props.wsId ?? props.workspaceId ?? 'default-workspace'} />
      </ViewConfigProvider>
    )
  }

  return (
    <KnowledgeBaseScreen
      workspaceLabel={props.wsId ?? props.workspaceId ?? 'default-workspace'}
      config={props.config}
      showHeader={props.showHeader}
    />
  )
}
